import { ClipLoader } from 'react-spinners';
import { AccentButton } from '../../../../../shared/Buttons';
import {
  CustomerWithDriverOnboarding,
  DropboxSignTemplateType,
  LoanRequestWithTerms,
} from '../../../../../types/apiTypes';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useState, useEffect } from 'react';
import HelloSign from 'hellosign-embedded';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

function CashAdvanceConsent() {
  const { screenView } = useScreenView();
  const [isSignatureComplete, setIsSignatureComplete] = useState(false);
  const [client, setClient] = useState<any>(null);
  const { callProtectedApi } = useApi();
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const { userRecord, userId } = useUser();
  const [loanRequest, setLoanRequest] = useState<LoanRequestWithTerms | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { request_id } = useParams();
  // Initialize HelloSign client
  useEffect(() => {
    if (loanRequest === null) {
      fetchLoanRequest();
    }

    if (loanRequest) {
      const helloSignClient = new HelloSign({
        clientId: process.env.REACT_APP_DROPBOX_SIGN_CLIENT_ID,
      });
      helloSignClient.on('finish', async () => {
        try {
          setIsSignatureComplete(true);
          queryClient.invalidateQueries({
            queryKey: ['pendingConsentLoanRequests'],
          });
          toast.success('Signature submitted successfully');
          navigate('/dashboard/home');
        } catch (error: any) {
          toast.error('Error occurred in signature submission.');
        }
      });
      helloSignClient.on('close', () => {
        setSignUrl(null);
      });
      setClient(helloSignClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanRequest]);

  const fetchLoanRequest = async () => {
    setIsLoading(true);
    try {
      const response = await callProtectedApi(
        `/credit/loan-request/${request_id ?? ''}/consent`,
        'get',
        {},
        'Error occurred fetching the details of this cash advance request.'
      );

      const loanRequest = response?.data?.data?.loanRequest;
      if (
        loanRequest &&
        Object.entries(loanRequest).length > 0 &&
        response?.status === 200
      ) {
        if (
          loanRequest?.loanTermsOptions &&
          loanRequest?.loanTermsOptions?.length > 0
        ) {
          setLoanRequest(loanRequest);
        } else {
          toast.error('This page does not exist.');
          navigate('/dashboard/home');
        }
      }
    } catch (error: any) {
      console.error('Error initiating signing process:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const initiateSigningProcess = async () => {
    setIsLoading(true);
    try {
      const response = await callProtectedApi(
        `/documents/createSignatureRequest/${userId ?? ''}`,
        'post',
        {
          email: userRecord?.email,
          name: (userRecord as CustomerWithDriverOnboarding)?.business_name,
          role: 'carrier',
          metadata: {
            customer_id: userId,
            loan_request_id: request_id,
            terms_id: loanRequest?.loanTermsOptions[0]?.terms_id,
            funding_source: loanRequest?.loanTermsOptions[0]?.funding_source,
          },
          templateType: DropboxSignTemplateType.CashAdvanceAgreement,
        }
      );

      const signUrlOut = await response?.data?.data?.sign_url;
      setSignUrl(signUrlOut);
      const isLocal = window.location.hostname === 'localhost';
      if (client && signUrlOut) {
        client.open(signUrlOut, {
          skipDomainVerification: isLocal,
          testMode: isLocal,
          container: document.getElementById('signature-container'),
          height: '100%',
          width: '100%',
        });
      }
    } catch (error: any) {
      console.error('Error initiating signing process:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (!isSignatureComplete) {
      return (
        <div className="relative flex h-full w-full flex-col items-center justify-start gap-y-[16px]">
          {signUrl === null && (
            <>
              <h1 className="mb-2 justify-center text-center text-[32px] font-semibold text-primary">
                Sign Cash Advance Agreement
              </h1>
              <h2 className="text-center text-[20px] text-graysecondary">
                Please review and sign your cash advance agreement
              </h2>
              <AccentButton
                addClass="rounded-full w-[200px] h-[48px] space-x-2 flex flex-row items-center justify-center"
                onClick={initiateSigningProcess}
              >
                <p>Sign Document</p>
                <ClipLoader
                  color="#1D213B"
                  loading={isLoading}
                  size={20}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              </AccentButton>
            </>
          )}
          <div
            id="signature-container"
            className={`absolute left-0 top-0 h-[100%] w-[100%] ${signUrl !== null ? 'z-40' : 'pointer-events-none'}`}
          ></div>
        </div>
      );
    }

    return (
      <>
        <div className="flex w-full flex-col items-center justify-center gap-y-[16px]">
          <h1 className="justify-center text-center text-[32px] font-semibold text-primary">
            Processing your signature...
          </h1>
          <p>This may take a few moments</p>
          <ClipLoader
            color="#1D213B"
            loading={true}
            size={60}
            aria-label="Loading Spinner"
            data-testid="loader"
            className="h-full w-full items-center justify-center"
          />
        </div>
      </>
    );
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex h-full w-full flex-col items-center ${signUrl === null ? 'pt-[7rem]' : ''}`}
          >
            {renderContent()}
          </div>
        );
      case 'tablet':
      case 'mobile':
        return (
          <div
            className={`flex h-full w-full flex-col items-center ${signUrl === null ? 'pt-[7rem]' : ''}`}
          >
            {renderContent()}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default CashAdvanceConsent;
